/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react/jsx-wrap-multilines */
import React, { ComponentType, useCallback, useMemo } from 'react';
import { Route, Routes, HashRouter, Outlet, useNavigate } from 'react-router-dom';
import { useIntl } from 'react-intl';
import { PrivateRoute, StandardRoutes } from '@sdir/auth/lib/components';
import { useAuthentication, useCurrentUser } from '@sdir/auth/lib/hooks';
import {
  SdiEditText,
  SdiLicense,
  SdirFooter,
  SdirHeader,
  SdiSettings,
  SkipLink,
  styles,
} from '@sdir/sds';
import { routes } from './constants';
import {
  ApplicantPage,
  CodePage,
  CRAPage,
  HomePage,
  NationalCertificatePage,
  PaymentPage,
  UserManagementPage,
  RequirementsPage,
} from '../Pages';
import SailorPage from '../Pages/SailorPage';
import ApplicationPage from '../Pages/ApplicationPage';
import { CurrentApplicationProvider } from '../Providers/CurrentApplicationProvider';
import { CertificatesProvider } from '../Providers/CertificatesProvider';
import SummaryPage from '../Pages/SummaryPage';
import { AuthoritiesProvider } from '../Providers/AuthoritiesProvider';
import { NationalitiesProvider } from '../Providers/NationalitiesProvider';
import { checkIsOrganization } from '../../constants/permissions';

interface ApplicationProvidersProps {
  children?: any;
}

const ApplicationProviders: React.FC<ApplicationProvidersProps> = () => {
  return (
    <CertificatesProvider>
      <AuthoritiesProvider>
        <NationalitiesProvider>
          <CurrentApplicationProvider>
            <Outlet />
          </CurrentApplicationProvider>
        </NationalitiesProvider>
      </AuthoritiesProvider>
    </CertificatesProvider>
  );
};

const HomePageProviders: React.FC<ApplicationProvidersProps> = () => {
  return (
    <NationalitiesProvider>
      <Outlet />
    </NationalitiesProvider>
  );
};

interface RouterProps {
  ContentSection: ComponentType<any>;
}

const RoutesList: React.FC<RouterProps> = ({ ContentSection }) => {
  const currentUser = useCurrentUser();

  const isOrganization = useMemo(() => {
    return checkIsOrganization(currentUser);
  }, [currentUser]);

  return (
    <HashRouter>
      <SdirHeaderWrapper isOrganization={isOrganization} currentUser={currentUser} />
      <ContentSection>
        <Routes>
          <Route element={<HomePageProviders />}>
            <Route
              path={routes.home}
              element={
                <PrivateRoute>
                  <HomePage />
                </PrivateRoute>
              }
            />
          </Route>
          {!isOrganization && (
            <Route
              path={routes.code}
              element={
                <PrivateRoute>
                  <CodePage />
                </PrivateRoute>
              }
            />
          )}
          {isOrganization && (
            <Route
              path={routes.userManagement}
              element={
                <PrivateRoute>
                  <UserManagementPage />
                </PrivateRoute>
              }
            />
          )}
          <Route element={<ApplicationProviders />}>
            <Route
              path={routes.applicationNew}
              element={
                <PrivateRoute>
                  <ApplicantPage />
                </PrivateRoute>
              }
            />
          </Route>
          <Route element={<ApplicationProviders />}>
            <Route path={routes.applicationBase}>
              <Route path={routes.applicationBase} element={<ApplicationPage />} />
            </Route>
            <Route
              path={routes.applicant}
              element={
                <PrivateRoute>
                  <ApplicantPage />
                </PrivateRoute>
              }
            />
            <Route
              path={routes.sailor}
              element={
                <PrivateRoute>
                  <SailorPage />
                </PrivateRoute>
              }
            />
            <Route
              path={routes.certificate}
              element={
                <PrivateRoute>
                  <NationalCertificatePage />
                </PrivateRoute>
              }
            />
            <Route
              path={routes.requirements}
              element={
                <PrivateRoute>
                  <RequirementsPage />
                </PrivateRoute>
              }
            />
            <Route
              path={routes.cra}
              element={
                <PrivateRoute>
                  <CRAPage />
                </PrivateRoute>
              }
            />
            <Route
              path={routes.summary}
              element={
                <PrivateRoute>
                  <SummaryPage />
                </PrivateRoute>
              }
            />
            <Route
              path={routes.payment}
              element={
                <PrivateRoute>
                  <PaymentPage />
                </PrivateRoute>
              }
            />
          </Route>
          <Route path="*" element={<StandardRoutes />} />
        </Routes>
      </ContentSection>
      <SdirFooterWrapper />
    </HashRouter>
  );
};

export default RoutesList;

const SdirHeaderWrapper = ({ isOrganization, currentUser }) => {
  const intl = useIntl();
  const navigate = useNavigate();
  const authenticationService = useAuthentication();

  const menuItems: any[] = useMemo(() => {
    const items: any[] = [];
    if (isOrganization) {
      items.push({
        icon: <SdiSettings color={styles.colors.primary} size="s" />,
        text: 'User management',
        type: 'button',
        onClick: () => {
          navigate(routes.userManagement);
        },
      });
    }
    if (!isOrganization) {
      items.push({
        icon: <SdiEditText color={styles.colors.primary} size="s" />,
        text: 'Register by code',
        type: 'button',
        onClick: () => {
          navigate(routes.code);
        },
      });
    }
    items.push({
      icon: <SdiLicense color={styles.colors.primary} size="s" />,
      text: 'Home',
      type: 'button',
      onClick: () => {
        navigate(routes.home);
      },
    });
    return items;
  }, [isOrganization]);

  const onLogout = useCallback(() => {
    authenticationService?.logout();
  }, []);

  return (
    <>
      <SkipLink
        linkText={intl.formatMessage({ id: 'userbar.skiplink' })}
        mainContentId="msp-main"
      />
      <SdirHeader
        appName={intl.formatMessage({ id: 'app.title' })}
        backLink={{ url: 'https://sdir.no', text: intl.formatMessage({ id: 'userbar.sdirlink' }) }}
        hasLogin
        isLoggedIn
        loggedInAs={{
          username: currentUser?.profile?.name || '',
          identification: currentUser?.profile?.reportee || '',
        }}
        maxContentWidth="1400px"
        menuToggleText={intl.formatMessage({ id: 'userbar.menu' })}
        navMenuItems={menuItems}
        onLogOutClick={onLogout}
      />
    </>
  );
};

const SdirFooterWrapper = () => {
  const intl = useIntl();
  const aboutLinks = useMemo(() => {
    return [
      {
        text: intl.formatMessage({ id: 'footer.about.privacy' }),
        url: 'https://www.sdir.no/om-oss/personvernerklaring/',
      },
      {
        text: intl.formatMessage({ id: 'footer.about.accessibility' }),
        url: 'https://uustatus.no/nb/erklaringer/publisert/d4ac9af4-b441-4e05-b62a-207686c3f8cc',
        type: 'external' as any,
      },
    ];
  }, [intl]);

  const socialLinks = useMemo(() => {
    return [
      {
        text: intl.formatMessage({ id: 'footer.social.facebook' }),
        url: 'https://www.facebook.com/sjofartsdir',
        type: 'facebook' as any,
      },
      {
        text: intl.formatMessage({ id: 'footer.social.instagram' }),
        url: 'https://www.instagram.com/sjofartsdir/',
        type: 'instagram' as any,
      },
      {
        text: intl.formatMessage({ id: 'footer.social.linkedin' }),
        url: 'https://www.linkedin.com/company/41031/',
        type: 'linkedin' as any,
      },
    ];
  }, [intl]);

  return (
    <SdirFooter
      tagline={intl.formatMessage({ id: 'footer.tagline' })}
      maxContentWidth="1400px"
      aboutHeader={intl.formatMessage({ id: 'footer.about.title' })}
      aboutLinks={aboutLinks}
      socialHeader={intl.formatMessage({ id: 'footer.social.title' })}
      socialLinks={socialLinks}
    />
  );
};
